import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { 
  CONVERSION_RATIOS,
  ELEMENT_SPACING,
  FILE_SETTINGS,
  IMAGE_SETTINGS,
  PAGE_DIMENSIONS,
  PdfExportFormat,
  PdfExportOptions,
  PdfExportOrientation,  
  PdfExportResult,
  RENDERING_DELAYS,
  SCALE_LIMITS,
  TEMP_CONTAINER_POSITION,
} from './types';

const DEFAULT_OPTIONS: Required<PdfExportOptions> = {
  filename: FILE_SETTINGS.DEFAULT_FILENAME,
  scale: 1,
  backgroundColor: FILE_SETTINGS.DEFAULT_BG_COLOR,
  format: PdfExportFormat.LETTER,
  orientation: PdfExportOrientation.LANDSCAPE,
  useCORS: true,
  allowTaint: true,
  width: 0,
  height: 0,
  quality: IMAGE_SETTINGS.MAX_QUALITY,
  includeScrollbars: false,
  returnPdfInMemory: false,
};

const TEMP_CONTAINER_STYLES = {
  position: 'absolute',
  left: TEMP_CONTAINER_POSITION.OFF_SCREEN_LEFT,
  top: TEMP_CONTAINER_POSITION.TOP_POSITION,
  backgroundColor: FILE_SETTINGS.DEFAULT_BG_COLOR,
  padding: `${ELEMENT_SPACING.TEMP_CONTAINER_PADDING_PX}px`,
  boxSizing: 'border-box',
  overflow: 'visible',
} as const;

const CLONED_ELEMENT_STYLES = {
  position: 'static',
  transform: 'none',
  margin: '0',
  padding: '0',
  width: '100%',
  maxWidth: 'none',
  minWidth: 'none',
  boxSizing: 'border-box',
  overflow: 'visible',
} as const;

/**
 * Resolve element from various input types
 */
const resolveElement = (element: HTMLElement | string | React.RefObject<HTMLElement | null>): HTMLElement | null => {
  if (typeof element === 'string') {
    // Treat string as data-ref name and look for element with that data-ref attribute
    return document.querySelector(`[data-ref="${element}"]`);
  }
  if (element && 'current' in element) {
    return element.current;
  }
  if (element instanceof HTMLElement) {
    return element;
  }
  
  // Fallback: check if it's an element-like object (for cross-document elements)
  if (element && typeof element === 'object' && (element as any).nodeType === 1) {
    return element as HTMLElement;
  }
  
  return null;
};

/**
 * Create and configure temporary container
 */
const createTempContainer = (): HTMLElement => {
  const container = document.createElement('div');

  Object.assign(container.style, TEMP_CONTAINER_STYLES, {
    width: 'auto',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
  });
  document.body.appendChild(container);
  return container;
};

/**
 * Clone and style elements for PDF export
 */
const mergeElements = (elements: HTMLElement[], container: HTMLElement): void => {
  elements.forEach((element, index) => {
    const clonedElement = element.cloneNode(true) as HTMLElement;
    Object.assign(clonedElement.style, CLONED_ELEMENT_STYLES);

    if (index > 0) {
      clonedElement.style.marginTop = `${ELEMENT_SPACING.ELEMENT_MARGIN_TOP_PX}px`;
    }

    container.appendChild(clonedElement);
  });
};

/**
 * Export multiple elements as a single continuous page in a PDF
 */
export const exportElements = async (
  elements: (HTMLElement | string | React.RefObject<HTMLElement | null>)[],
  options: PdfExportOptions = {}
): Promise<PdfExportResult> => {
  try {
    const config = { ...DEFAULT_OPTIONS, ...options };

    // Resolve and validate elements
    const validElements: HTMLElement[] = [];
    let maxWidth = 0;

    elements.forEach(element => {
      const targetElement = resolveElement(element);
      if (targetElement) {
        validElements.push(targetElement);
        const { width } = targetElement.getBoundingClientRect();
        const { scrollWidth } = targetElement;
        maxWidth = Math.max(maxWidth, Math.max(width, scrollWidth));
      }
    });

    if (validElements.length === 0) {
      return { success: false, error: 'No valid elements found to export' };
    }

    // Create temporary container and clone elements
    const tempContainer = createTempContainer();
    mergeElements(validElements, tempContainer);

    // Wait for rendering with longer timeout for Digital format
    await new Promise<void>(resolve => {
      const delay = config.format === PdfExportFormat.DIGITAL 
        ? RENDERING_DELAYS.DIGITAL_DELAY_MS 
        : RENDERING_DELAYS.DEFAULT_DELAY_MS;
      setTimeout(() => resolve(), delay);
    });

    // Generate canvas with adaptive scaling for Digital format
    const baseScale = config.scale;
    const adaptiveScale =
      config.format === PdfExportFormat.DIGITAL
        ? Math.max(
          baseScale,
          Math.max(
            SCALE_LIMITS.DIGITAL_MIN_SCALE, 
            Math.min(
              SCALE_LIMITS.DIGITAL_MAX_SCALE, 
              SCALE_LIMITS.BASE_SCALE_WIDTH_PX / Math.max(tempContainer.scrollWidth, tempContainer.scrollHeight)
            )
          )
        )
        : baseScale;

    const canvasOptions = {
      scale: adaptiveScale,
      backgroundColor: config.backgroundColor,
      useCORS: config.useCORS,
      allowTaint: config.allowTaint,
      width: tempContainer.scrollWidth,
      height: tempContainer.scrollHeight,      
      removeContainer: false,
    };

    const canvas = await html2canvas(tempContainer, canvasOptions);

    // Clean up
    document.body.removeChild(tempContainer);

    // Create PDF with custom dimensions for Digital format
    let pdf: JsPDF;
    if (config.format === PdfExportFormat.DIGITAL) {
      // Convert canvas dimensions from pixels to mm for Digital format
      const contentWidthMm = canvas.width / CONVERSION_RATIOS.MM_TO_PX;
      const contentHeightMm = canvas.height / CONVERSION_RATIOS.MM_TO_PX;

      // Use Letter as minimum, but expand if content is larger
      // Only apply minimum if content is actually smaller
      const finalWidthMm = contentWidthMm < PAGE_DIMENSIONS.LETTER_WIDTH_MM ? PAGE_DIMENSIONS.LETTER_WIDTH_MM : contentWidthMm;
      const finalHeightMm = contentHeightMm < PAGE_DIMENSIONS.LETTER_HEIGHT_MM ? PAGE_DIMENSIONS.LETTER_HEIGHT_MM : contentHeightMm;

      pdf = new JsPDF({
        unit: FILE_SETTINGS.PDF_UNIT,
        format: [finalWidthMm, finalHeightMm], // Letter minimum, expand if needed
        compress: false, // Disable compression to maintain exact appearance
      });
    } else {
      pdf = new JsPDF({
        unit: FILE_SETTINGS.PDF_UNIT,
        orientation: config.orientation,
        format: config.format,
      });
    }

    // Use maximum quality for Digital format to maintain exact DOM appearance
    const imgData = canvas.toDataURL('image/png', IMAGE_SETTINGS.MAX_QUALITY);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    if (config.format === PdfExportFormat.DIGITAL) {
      // For Digital format, scale content to fit within PDF page while maintaining aspect ratio
      const scaleFactor = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
      const finalImgWidth = canvas.width * scaleFactor;
      const finalImgHeight = canvas.height * scaleFactor;

      // Center content if it's smaller than Letter size
      const contentWidthMm = canvas.width / CONVERSION_RATIOS.MM_TO_PX;
      const contentHeightMm = canvas.height / CONVERSION_RATIOS.MM_TO_PX;

      const xOffset = contentWidthMm < PAGE_DIMENSIONS.LETTER_WIDTH_MM ? (pdfWidth - finalImgWidth) / 2 : 0;
      const yOffset = contentHeightMm < PAGE_DIMENSIONS.LETTER_HEIGHT_MM ? (pdfHeight - finalImgHeight) / 2 : 0;

      pdf.addImage(imgData, IMAGE_SETTINGS.PNG_FORMAT, xOffset, yOffset, finalImgWidth, finalImgHeight);
    } else {
      // For standard formats, scale content to fit within PDF page while maintaining aspect ratio
      const scaleFactor = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height);
      const finalImgWidth = canvas.width * scaleFactor;
      const finalImgHeight = canvas.height * scaleFactor;

      // Position content at top-left corner
      pdf.addImage(imgData, IMAGE_SETTINGS.PNG_FORMAT, 0, 0, finalImgWidth, finalImgHeight);
    }

    const filename = `${config.filename}.pdf`;
    
    // If returnPdfInMemory is true, return the PDF instance instead of downloading
    if (config.returnPdfInMemory) {
      return { success: true, filename, pdfInstance: pdf };
    }
    
    pdf.save(filename);
    return { success: true, filename };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown PDF export error occurred',
    };
  }
};
